import * as THREE from 'three';
import type { ModelProps, ModelRegistry } from '../types';

// ============================================
// STRANGER THINGS - ENVIRONMENT & UPSIDE DOWN
// ============================================

// ===== HAWKINS SUBURBAN ENVIRONMENT =====

export function StreetlampModel({ world }: ModelProps) {
  const lampOn = world === 'real';
  return (
    <group>
      <mesh position={[0, 1.5, 0]} castShadow>
        <cylinderGeometry args={[0.05, 0.08, 3, 8]} />
        <meshStandardMaterial color="#2F2F2F" metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0.2, 2.9, 0]} rotation={[0, 0, -0.5]} castShadow>
        <cylinderGeometry args={[0.03, 0.03, 0.5, 6]} />
        <meshStandardMaterial color="#2F2F2F" metalness={0.8} roughness={0.3} />
      </mesh>
      <mesh position={[0.35, 2.8, 0]} castShadow>
        <coneGeometry args={[0.15, 0.2, 8]} />
        <meshStandardMaterial color="#3a3a3a" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh position={[0.35, 2.68, 0]}>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshStandardMaterial
          color={lampOn ? "#FFFACD" : "#333333"}
          emissive={lampOn ? "#FFD700" : "#000000"}
          emissiveIntensity={lampOn ? 0.8 : 0}
          transparent
          opacity={0.9}
        />
      </mesh>
      {lampOn && (
        <pointLight position={[0.35, 2.68, 0]} intensity={0.5} distance={5} color="#FFD700" />
      )}
    </group>
  );
}

export function SuburbanHouseModel({ properties, isOccluding, opacity = 1 }: ModelProps) {
  const houseColor = properties?.variant === 'blue' ? '#6B8FAF' :
                     properties?.variant === 'brown' ? '#8B7355' : '#D4C4B0';
  return (
    <group>
      <mesh position={[0, 0.6, 0]} castShadow>
        <boxGeometry args={[1.5, 1.2, 1]} />
        <meshStandardMaterial color={houseColor} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 1.4, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[1.2, 0.6, 4]} />
        <meshStandardMaterial color="#5D3A1A" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.3, 0.4, 0.51]}>
        <boxGeometry args={[0.25, 0.5, 0.02]} />
        <meshStandardMaterial color="#4A2810" roughness={0.9} />
      </mesh>
      <mesh position={[-0.35, 0.6, 0.51]}>
        <boxGeometry args={[0.3, 0.25, 0.02]} />
        <meshStandardMaterial color="#87CEEB" transparent opacity={0.7} />
      </mesh>
      <mesh position={[0, 0.9, 0.51]}>
        <boxGeometry args={[0.25, 0.2, 0.02]} />
        <meshStandardMaterial color="#87CEEB" transparent opacity={0.7} />
      </mesh>
      <mesh position={[0.5, 1.5, -0.2]} castShadow>
        <boxGeometry args={[0.2, 0.4, 0.2]} />
        <meshStandardMaterial color="#8B4513" roughness={0.9} transparent={isOccluding} opacity={opacity} />
      </mesh>
    </group>
  );
}

export function ChristmasLightsModel(): JSX.Element {
  return (
    <group>
      <mesh position={[0, 0, 0]} rotation={[0, 0, 0.1]}>
        <cylinderGeometry args={[0.01, 0.01, 2, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {[-0.8, -0.5, -0.2, 0.1, 0.4, 0.7].map((x, i) => {
        const lightColors = ['#FF0000', '#FFFF00', '#00FF00', '#0000FF', '#FF00FF', '#00FFFF'];
        const isOn = Math.random() > 0.3;
        return (
          <mesh key={`light-${i}`} position={[x, -0.05, 0]}>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial
              color={lightColors[i]}
              emissive={lightColors[i]}
              emissiveIntensity={isOn ? 0.8 : 0.1}
            />
          </mesh>
        );
      })}
    </group>
  );
}

export function RoadSegmentModel({ properties }: ModelProps) {
  const roadLength = (properties?.length as number) || 6;
  const roadWidth = (properties?.width as number) || 2;
  return (
    <group>
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[roadLength, roadWidth]} />
        <meshStandardMaterial color="#2a2a2a" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.025, roadWidth / 2 - 0.08]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[roadLength, 0.1]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.8} />
      </mesh>
      <mesh position={[0, 0.025, -roadWidth / 2 + 0.08]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[roadLength, 0.1]} />
        <meshStandardMaterial color="#FFFFFF" roughness={0.8} />
      </mesh>
      {Array.from({ length: Math.floor(roadLength / 1.2) }).map((_, i) => (
        <mesh
          key={`center-line-${i}`}
          position={[-roadLength / 2 + 0.6 + i * 1.2, 0.025, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <planeGeometry args={[0.8, 0.08]} />
          <meshStandardMaterial color="#FFD700" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

export function ElectricPylonModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const pylonHeight = 5;
  const pylonRust = world === 'spectral';
  const pylonColor = pylonRust ? '#4a3a2a' : '#5a5a5a';
  const wireColor = pylonRust ? '#3a3a3a' : '#1a1a1a';
  return (
    <group>
      {[[-0.4, 0.4], [-0.4, -0.4], [0.4, 0.4], [0.4, -0.4]].map((pos, i) => (
        <mesh key={`leg-${i}`} position={[pos[0], pylonHeight * 0.3, pos[1]]} rotation={[0, 0, pos[0] > 0 ? -0.15 : 0.15]} castShadow>
          <cylinderGeometry args={[0.04, 0.06, pylonHeight * 0.65, 6]} />
          <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
        </mesh>
      ))}
      <mesh position={[0, pylonHeight * 0.6, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.05, pylonHeight * 0.4, 6]} />
        <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[0.3, 0.5, 0.7].map((h, i) => (
        <group key={`cross-${i}`}>
          <mesh position={[0, pylonHeight * h, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.8 - i * 0.15, 4]} />
            <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
          </mesh>
          <mesh position={[0, pylonHeight * h, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.02, 0.02, 0.8 - i * 0.15, 4]} />
            <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, pylonHeight * 0.85, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.03, 0.03, 1.8, 6]} />
        <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, pylonHeight * 0.95, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.025, 0.025, 1.2, 6]} />
        <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {[-0.8, -0.4, 0.4, 0.8].map((x, i) => (
        <group key={`insulator-${i}`} position={[x, pylonHeight * 0.85, 0]}>
          <mesh>
            <cylinderGeometry args={[0.04, 0.03, 0.15, 8]} />
            <meshStandardMaterial color="#4a6a5a" roughness={0.7} />
          </mesh>
          <mesh position={[0, -0.2, 0]}>
            <cylinderGeometry args={[0.008, 0.008, 0.25, 4]} />
            <meshStandardMaterial color={wireColor} metalness={0.8} />
          </mesh>
        </group>
      ))}
      {[-0.5, 0.5].map((x, i) => (
        <group key={`insulator-top-${i}`} position={[x, pylonHeight * 0.95, 0]}>
          <mesh>
            <cylinderGeometry args={[0.035, 0.025, 0.12, 8]} />
            <meshStandardMaterial color="#4a6a5a" roughness={0.7} />
          </mesh>
          <mesh position={[0, -0.15, 0]}>
            <cylinderGeometry args={[0.008, 0.008, 0.2, 4]} />
            <meshStandardMaterial color={wireColor} metalness={0.8} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, pylonHeight, 0]}>
        <coneGeometry args={[0.06, 0.15, 6]} />
        <meshStandardMaterial color={pylonColor} metalness={0.6} roughness={0.5} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {pylonRust && (
        <>
          {[[0.2, 2, 0.1], [-0.15, 2.5, -0.1], [0.1, 3.5, 0.15]].map((pos, i) => (
            <mesh key={`pylon-vine-${i}`} position={pos as [number, number, number]} rotation={[0.2 * i, 0.3 * i, 0]}>
              <cylinderGeometry args={[0.015, 0.02, 1 + i * 0.3, 5]} />
              <meshStandardMaterial color="#1a2a1a" roughness={0.9} />
            </mesh>
          ))}
        </>
      )}
    </group>
  );
}

export function PowerLineModel({ properties }: ModelProps) {
  const lineLength = (properties?.length as number) || 8;
  const lineSag = (properties?.sag as number) || 0.5;
  return (
    <group>
      {[-0.3, 0, 0.3].map((offset, i) => (
        <mesh key={`wire-${i}`} position={[0, -lineSag / 2, offset]}>
          <tubeGeometry args={[
            new THREE.CatmullRomCurve3([
              new THREE.Vector3(-lineLength / 2, lineSag, 0),
              new THREE.Vector3(-lineLength / 4, 0, 0),
              new THREE.Vector3(0, -lineSag * 0.3, 0),
              new THREE.Vector3(lineLength / 4, 0, 0),
              new THREE.Vector3(lineLength / 2, lineSag, 0),
            ]),
            20, 0.01, 4
          ]} />
          <meshStandardMaterial color="#1a1a1a" metalness={0.8} roughness={0.4} />
        </mesh>
      ))}
    </group>
  );
}

// ===== NEON SIGNS & LIGHTING =====

export function NeonSignModel({ properties }: ModelProps) {
  const neonColor = properties?.color === 'blue' ? '#00BFFF' :
                    properties?.color === 'red' ? '#FF1744' :
                    properties?.color === 'purple' ? '#E040FB' : '#00BFFF';
  const neonGlow = properties?.color === 'blue' ? '#0080FF' :
                   properties?.color === 'red' ? '#FF0000' :
                   properties?.color === 'purple' ? '#AA00FF' : '#0080FF';
  const neonShape = properties?.shape || 'line';
  return (
    <group>
      {neonShape === 'line' && (
        <>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.025, 0.025, 1.5, 8]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} transparent opacity={0.95} />
          </mesh>
          <mesh position={[-0.7, 0, -0.05]}>
            <boxGeometry args={[0.04, 0.06, 0.08]} />
            <meshStandardMaterial color="#2a2a2a" metalness={0.8} />
          </mesh>
          <mesh position={[0.7, 0, -0.05]}>
            <boxGeometry args={[0.04, 0.06, 0.08]} />
            <meshStandardMaterial color="#2a2a2a" metalness={0.8} />
          </mesh>
          <pointLight color={neonGlow} intensity={2} distance={4} decay={2} />
        </>
      )}
      {neonShape === 'circle' && (
        <>
          <mesh>
            <torusGeometry args={[0.4, 0.025, 8, 32]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} transparent opacity={0.95} />
          </mesh>
          <mesh position={[0, 0.45, -0.05]}>
            <boxGeometry args={[0.06, 0.06, 0.08]} />
            <meshStandardMaterial color="#2a2a2a" metalness={0.8} />
          </mesh>
          <pointLight color={neonGlow} intensity={2.5} distance={5} decay={2} />
        </>
      )}
      {neonShape === 'arrow' && (
        <>
          <mesh position={[0.15, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.8, 6]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} />
          </mesh>
          <mesh position={[0.55, 0.15, 0]} rotation={[0, 0, -0.5]}>
            <cylinderGeometry args={[0.02, 0.02, 0.3, 6]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} />
          </mesh>
          <mesh position={[0.55, -0.15, 0]} rotation={[0, 0, 0.5]}>
            <cylinderGeometry args={[0.02, 0.02, 0.3, 6]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} />
          </mesh>
          <pointLight color={neonGlow} intensity={2} distance={4} decay={2} />
        </>
      )}
      {neonShape === 'x' && (
        <>
          <mesh rotation={[0, 0, Math.PI / 4]}>
            <cylinderGeometry args={[0.02, 0.02, 0.8, 6]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} />
          </mesh>
          <mesh rotation={[0, 0, -Math.PI / 4]}>
            <cylinderGeometry args={[0.02, 0.02, 0.8, 6]} />
            <meshStandardMaterial color={neonColor} emissive={neonGlow} emissiveIntensity={1.5} />
          </mesh>
          <pointLight color={neonGlow} intensity={2} distance={4} decay={2} />
        </>
      )}
    </group>
  );
}

export function NeonTextModel({ properties }: ModelProps) {
  const textNeonColor = properties?.color === 'blue' ? '#00BFFF' :
                        properties?.color === 'red' ? '#FF1744' : '#00BFFF';
  const textGlow = properties?.color === 'blue' ? '#0080FF' :
                   properties?.color === 'red' ? '#FF0000' : '#0080FF';
  return (
    <group>
      <mesh position={[0, 0, -0.05]}>
        <boxGeometry args={[1.2, 0.5, 0.02]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      <mesh position={[-0.4, 0, 0]}>
        <torusGeometry args={[0.12, 0.02, 6, 16]} />
        <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
      </mesh>
      <group position={[-0.12, 0, 0]}>
        <mesh position={[0, 0, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.25, 6]} />
          <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
        </mesh>
        <mesh position={[0.06, 0.06, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.06, 0.02, 6, 8, Math.PI]} />
          <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
        </mesh>
      </group>
      <group position={[0.15, 0, 0]}>
        <mesh>
          <cylinderGeometry args={[0.02, 0.02, 0.25, 6]} />
          <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
        </mesh>
        {[-0.1, 0, 0.1].map((y, i) => (
          <mesh key={`e-${i}`} position={[0.06, y, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.1, 6]} />
            <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
          </mesh>
        ))}
      </group>
      <group position={[0.42, 0, 0]}>
        <mesh position={[-0.06, 0, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.25, 6]} />
          <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
        </mesh>
        <mesh position={[0.06, 0, 0]}>
          <cylinderGeometry args={[0.02, 0.02, 0.25, 6]} />
          <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
        </mesh>
        <mesh rotation={[0, 0, 0.5]}>
          <cylinderGeometry args={[0.02, 0.02, 0.28, 6]} />
          <meshStandardMaterial color={textNeonColor} emissive={textGlow} emissiveIntensity={1.2} />
        </mesh>
      </group>
      <pointLight color={textGlow} intensity={3} distance={5} decay={2} />
    </group>
  );
}

// ===== UPSIDE DOWN - CORRUPTION & ORGANIC =====

export function UpsideDownVinesModel(): JSX.Element {
  return (
    <group>
      {[0, 1, 2, 3].map((i) => {
        const angle = (i * Math.PI * 2) / 4;
        const sway = Math.sin(i * 1.5) * 0.2;
        return (
          <group key={`vine-${i}`}>
            <mesh position={[Math.cos(angle) * 0.3, 1, Math.sin(angle) * 0.3]} rotation={[sway, angle, 0.2 + sway]} castShadow>
              <cylinderGeometry args={[0.03, 0.05, 2, 6]} />
              <meshStandardMaterial color="#2d1f3d" roughness={0.8} emissive="#1a0a2e" emissiveIntensity={0.1} />
            </mesh>
            <mesh position={[Math.cos(angle) * 0.35, 0.5, Math.sin(angle) * 0.35]} rotation={[sway * 2, angle, 0.5]}>
              <cylinderGeometry args={[0.01, 0.02, 0.4, 4]} />
              <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
            </mesh>
          </group>
        );
      })}
      <mesh position={[0, 0.1, 0]} castShadow>
        <dodecahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial color="#1a0a2e" roughness={0.9} emissive="#3d1a4d" emissiveIntensity={0.05} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={`spore-${i}`} position={[Math.cos(i * 1.3) * 0.4, 0.8 + i * 0.2, Math.sin(i * 1.3) * 0.4]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial color="#5a3a7a" emissive="#8844aa" emissiveIntensity={0.5} transparent opacity={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function UpsideDownGateModel({ hovered }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 1.2, 0]} castShadow>
        <torusGeometry args={[1, 0.15, 8, 24]} />
        <meshStandardMaterial color="#2d1f3d" roughness={0.8} emissive="#4a1a6a" emissiveIntensity={0.2} />
      </mesh>
      <mesh position={[0, 1.2, 0]}>
        <circleGeometry args={[0.9, 32]} />
        <meshStandardMaterial color="#0a0a1a" emissive="#FF0000" emissiveIntensity={hovered ? 0.6 : 0.3} transparent opacity={0.8} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={`lightning-${i}`} position={[Math.cos(i * 1.6) * 0.5, 1.2 + Math.sin(i * 2) * 0.3, 0.05]}>
          <boxGeometry args={[0.02, 0.3, 0.01]} />
          <meshStandardMaterial color="#FF0000" emissive="#FF0000" emissiveIntensity={0.8} />
        </mesh>
      ))}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh key={`particle-${i}`} position={[Math.cos(i * 1.1) * 0.7, 1.2 + Math.sin(i * 1.7) * 0.5, 0.1]}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshStandardMaterial color="#FF4444" emissive="#FF0000" emissiveIntensity={0.6} transparent opacity={0.7} />
        </mesh>
      ))}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[1.2, 1.3, 0.2, 12]} />
        <meshStandardMaterial color="#1a0a1a" roughness={0.9} />
      </mesh>
    </group>
  );
}

export function OrganicMembraneModel(): JSX.Element {
  return (
    <group>
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1, 16]} />
        <meshStandardMaterial color="#1a0a2e" roughness={0.95} emissive="#2d1a3d" emissiveIntensity={0.05} />
      </mesh>
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i * Math.PI * 2) / 6;
        return (
          <mesh key={`vein-${i}`} position={[Math.cos(angle) * 0.5, 0.03, Math.sin(angle) * 0.5]} rotation={[-Math.PI / 2, 0, angle]}>
            <boxGeometry args={[0.05, 0.8, 0.02]} />
            <meshStandardMaterial color="#3d1a4d" emissive="#5a2a7a" emissiveIntensity={0.15} />
          </mesh>
        );
      })}
      {[0, 1, 2].map((i) => (
        <mesh key={`pustule-${i}`} position={[Math.cos(i * 2.1) * 0.3, 0.05, Math.sin(i * 2.1) * 0.3]}>
          <sphereGeometry args={[0.08, 8, 8]} />
          <meshStandardMaterial color="#2d1a3d" emissive="#4a2a5a" emissiveIntensity={0.1} />
        </mesh>
      ))}
    </group>
  );
}

// ============================================
// MODEL REGISTRY
// ============================================

export const strangerThingsEnvironmentModels: ModelRegistry = {
  // Hawkins Suburban Environment
  streetlamp: StreetlampModel,
  suburban_house: SuburbanHouseModel,
  christmas_lights: ChristmasLightsModel,
  road_segment: RoadSegmentModel,
  electric_pylon: ElectricPylonModel,
  power_line: PowerLineModel,

  // Neon Signs & Lighting
  neon_sign: NeonSignModel,
  neon_text: NeonTextModel,

  // Upside Down - Corruption & Organic
  upside_down_vines: UpsideDownVinesModel,
  upside_down_gate: UpsideDownGateModel,
  organic_membrane: OrganicMembraneModel,
};
